import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactFlow } from "@xyflow/react";
import { FLOW_EVENT_NODE_TYPE } from "../core/edgeClassifier";
/**
 * Owns the editor's multi-select state machine. xyflow's selection store
 * fires `onSelectionChange` per-frame during drag/box-select; mirroring that
 * naively flashes the single-select detail panel open/closed. We gate the
 * mirror behind two refs (`isDragging`, `isBoxSelecting`) and converge after
 * the interaction ends.
 *
 * Drag-end effects (`onNodesChange`, `clearGuides`) are late-bound — the
 * component registers them each render via `registerDragEndEffects` because
 * the hooks that produce them (`useEditableFlow`, `useHelperLines`) run
 * *after* this selection hook. The stable `handleDragEnd` callback delegates
 * through an internal ref so xyflow never sees a fresh identity.
 */ export function useEditorSelection(params) {
    const { controlledSelectedNodeId, onSelectedNodeChange, onOpenAssignmentClick, hasDetailRenderer } = params;
    const [selectedNodeIds, setSelectedNodeIds] = useState(()=>new Set());
    const selectedNodeIdsRef = useRef(selectedNodeIds);
    selectedNodeIdsRef.current = selectedNodeIds;
    const isDraggingRef = useRef(false);
    const isBoxSelectingRef = useRef(false);
    const { getNodes } = useReactFlow();
    const dragEndImplRef = useRef(()=>{});
    const handleDragEnd = useCallback((movedIds)=>{
        dragEndImplRef.current(movedIds);
    }, []);
    const registerDragEndEffects = useCallback((effects)=>{
        dragEndImplRef.current = (_movedIds)=>{
            isDraggingRef.current = false;
            effects.clearGuides();
            const current = selectedNodeIdsRef.current;
            const changes = [];
            for (const n of getNodes()){
                const shouldBe = current.has(n.id);
                if (Boolean(n.selected) !== shouldBe) {
                    changes.push({
                        id: n.id,
                        type: "select",
                        selected: shouldBe
                    });
                }
            }
            if (changes.length > 0) effects.onNodesChange(changes);
        };
    }, [
        getNodes
    ]);
    const selectedNodeId = useMemo(()=>selectedNodeIds.size === 1 ? selectedNodeIds.values().next().value ?? null : null, [
        selectedNodeIds
    ]);
    const handleSelectionChange = useCallback(({ nodes: sel })=>{
        if (isDraggingRef.current || isBoxSelectingRef.current) return;
        const current = selectedNodeIdsRef.current;
        if (sel.length === current.size && sel.every((n)=>current.has(n.id))) {
            return;
        }
        const next = new Set(sel.map((n)=>n.id));
        setSelectedNodeIds(next);
        const single = next.size === 1 ? next.values().next().value ?? null : null;
        onSelectedNodeChange?.(single);
    }, [
        onSelectedNodeChange
    ]);
    // Selection for nodes that live OUTSIDE the controlled canvas state (sticky
    // notes): xyflow dispatches their marquee/click `select` changes through
    // `onNodesChange`, but `applyNodeChanges` can't land them anywhere — the
    // note layer routes them here instead so this model stays the single source
    // of truth. Mid-interaction the single-select notification is suppressed
    // (the detail panel would flash while the marquee is still drawn); the
    // selection-end handler converges and notifies.
    // Ids whose selection is externally managed and currently on — used by
    // `handleSelectionEnd` to survive the commit race (see below).
    const externalSelectedRef = useRef(new Set());
    const applyExternalSelectChanges = useCallback((changes)=>{
        for (const change of changes){
            if (change.selected) externalSelectedRef.current.add(change.id);
            else externalSelectedRef.current.delete(change.id);
        }
        const current = selectedNodeIdsRef.current;
        let next = null;
        for (const change of changes){
            const has = (next ?? current).has(change.id);
            if (change.selected === has) continue;
            next = next ?? new Set(current);
            if (change.selected) next.add(change.id);
            else next.delete(change.id);
        }
        if (!next) return;
        // Update the ref eagerly: `handleSelectionEnd` can fire in the same tick
        // (fast marquee) and must see this selection before React commits.
        selectedNodeIdsRef.current = next;
        setSelectedNodeIds(next);
        if (isDraggingRef.current || isBoxSelectingRef.current) return;
        onSelectedNodeChange?.(next.size === 1 ? next.values().next().value ?? null : null);
    }, [
        onSelectedNodeChange
    ]);
    // Controlled selection ("go to issue" from the issues sidebar) only
    // reconciles our Set — RF's internal selection is untouched, since these
    // flows navigate to a node, not stage it for keyboard delete.
    useEffect(function syncControlledSelection() {
        if (controlledSelectedNodeId === undefined) return;
        const current = selectedNodeIdsRef.current;
        const currentSingle = current.size === 1 ? current.values().next().value ?? null : null;
        if (currentSingle === controlledSelectedNodeId) return;
        setSelectedNodeIds(controlledSelectedNodeId ? new Set([
            controlledSelectedNodeId
        ]) : new Set());
    }, [
        controlledSelectedNodeId
    ]);
    const handleNodeClick = useCallback((event, node)=>{
        if (node.type === "flowViralPlaceholder") {
            const kind = node.data?.kind;
            if (kind === "open-assignment") {
                onOpenAssignmentClick?.(node.id);
                return;
            }
        }
        if (node.type === "flowStart" && !hasDetailRenderer) return;
        if (node.type === FLOW_EVENT_NODE_TYPE && !hasDetailRenderer) return;
        // Shift+click defers to RF's onSelectionChange so multi-selection
        // accumulates correctly.
        if (event.shiftKey) return;
        const prev = selectedNodeIdsRef.current;
        if (prev.size === 1 && prev.has(node.id)) return;
        setSelectedNodeIds(new Set([
            node.id
        ]));
        onSelectedNodeChange?.(node.id);
    }, [
        onOpenAssignmentClick,
        onSelectedNodeChange,
        hasDetailRenderer
    ]);
    const handlePaneClick = useCallback(()=>{
        if (selectedNodeIdsRef.current.size === 0) return;
        setSelectedNodeIds(new Set());
        onSelectedNodeChange?.(null);
    }, [
        onSelectedNodeChange
    ]);
    const handleNodeDragStart = useCallback(()=>{
        isDraggingRef.current = true;
    }, []);
    const handleSelectionStart = useCallback(()=>{
        isBoxSelectingRef.current = true;
    }, []);
    const handleSelectionEnd = useCallback(()=>{
        isBoxSelectingRef.current = false;
        const sel = getNodes().filter((n)=>n.selected);
        const current = selectedNodeIdsRef.current;
        const next = new Set(sel.map((n)=>n.id));
        // Externally-managed nodes (sticky notes) get their `selected` stamped in
        // OUR render, so `getNodes()` misses one selected during the mouseup
        // frame — merge them from the routed select changes, gated on the current
        // set so a selection cleared elsewhere can't resurrect.
        for (const id of externalSelectedRef.current){
            if (current.has(id)) next.add(id);
        }
        if (next.size === current.size && [
            ...next
        ].every((id)=>current.has(id))) {
            return;
        }
        setSelectedNodeIds(next);
        const single = next.size === 1 ? next.values().next().value ?? null : null;
        onSelectedNodeChange?.(single);
    }, [
        getNodes,
        onSelectedNodeChange
    ]);
    const closeDetailPanel = useCallback(()=>{
        if (selectedNodeIdsRef.current.size === 0) return;
        setSelectedNodeIds(new Set());
        onSelectedNodeChange?.(null);
    }, [
        onSelectedNodeChange
    ]);
    return {
        selectedNodeIds,
        selectedNodeId,
        handleDragEnd,
        registerDragEndEffects,
        handleSelectionChange,
        applyExternalSelectChanges,
        handleNodeClick,
        handlePaneClick,
        handleNodeDragStart,
        handleSelectionStart,
        handleSelectionEnd,
        closeDetailPanel
    };
}

//# sourceMappingURL=useEditorSelection.js.map